/**
 * MODERATION QUEUE
 *
 * Admin page to triage user-submitted reports. Reports come from the mobile
 * app's report sheets across marketplace items, creations, profiles, chat
 * messages, and community polish photos.
 *
 * Required for App Store compliance (guideline 1.2 + 5.1.1). Without this,
 * reports go to a Firestore collection that nothing reads.
 *
 * Each report lands as pending → admin reviews + acts → status flips to
 * resolved/dismissed with optional notes. Hide-content action sets
 * `isDeleted=true` on the target via the existing soft-delete helpers.
 *
 * REQUIRES: isAdmin: true on your user document in Firestore.
 *
 * Route: /admin/moderation
 */

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../firebase/config';
import {
  Shield,
  ArrowLeft,
  RefreshCw,
  AlertTriangle,
  Check,
  X,
  EyeOff,
  FileText,
  User,
  MessageSquare,
  Image as ImageIcon,
  ShoppingBag,
  Sparkles,
  Clock,
  Ban,
} from 'lucide-react';

// ---- Types ----------------------------------------------------------------

type ReportTargetType = 'item' | 'creation' | 'profile' | 'message' | 'photo';
type ReportReason =
  | 'harassment'
  | 'inappropriate'
  | 'spam'
  | 'scam'
  | 'impersonation'
  | 'other';
type ReportStatus = 'pending' | 'resolved' | 'dismissed';

interface ReportContext {
  conversationId?: string;
  polishSlug?: string;
  // Open shape — surfaces can attach anything useful here.
  [k: string]: unknown;
}

interface Report {
  id: string;
  reporterId: string;
  targetType: ReportTargetType;
  targetId: string;
  targetOwnerId: string;
  reason: ReportReason;
  detail?: string;
  context?: ReportContext;
  createdAt?: Timestamp;
  status: ReportStatus;
  resolvedAt?: Timestamp;
  resolvedBy?: string;
  resolution?: string;
}

interface UserChip {
  uid: string;
  username?: string;
  displayName?: string;
  avatarUrl?: string;
  moderationState?: 'active' | 'suspended' | 'banned';
}

// ---- Component ------------------------------------------------------------

const TARGET_TYPE_META: Record<
  ReportTargetType,
  { label: string; Icon: typeof FileText; color: string }
> = {
  item: { label: 'Marketplace item', Icon: ShoppingBag, color: 'text-emerald-300' },
  creation: { label: 'Creation', Icon: Sparkles, color: 'text-purple-300' },
  profile: { label: 'Profile', Icon: User, color: 'text-blue-300' },
  message: { label: 'Chat message', Icon: MessageSquare, color: 'text-amber-300' },
  photo: { label: 'Community photo', Icon: ImageIcon, color: 'text-pink-300' },
};

const REASON_LABEL: Record<ReportReason, string> = {
  harassment: 'Harassment',
  inappropriate: 'Inappropriate content',
  spam: 'Spam',
  scam: 'Scam',
  impersonation: 'Impersonation',
  other: 'Other',
};

function formatDate(t?: Timestamp): string {
  if (!t) return '—';
  return t.toDate().toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function ModerationQueue() {
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const [tab, setTab] = useState<'pending' | 'resolved'>('pending');
  const [reports, setReports] = useState<Report[]>([]);
  const [users, setUsers] = useState<Record<string, UserChip>>({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState<Record<string, string>>({});

  // Admin gate — same pattern as BrandClaimsAdmin.
  useEffect(() => {
    return onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user?.uid ?? null);
      if (user) {
        const snap = await getDoc(doc(db, 'users', user.uid));
        setIsAdmin(snap.exists() && snap.data()?.isAdmin === true);
      } else {
        setIsAdmin(false);
      }
      setCheckingAdmin(false);
    });
  }, []);

  // Load reports for the active tab.
  const loadReports = async () => {
    setLoading(true);
    try {
      const statusFilter: ReportStatus[] =
        tab === 'pending' ? ['pending'] : ['resolved', 'dismissed'];
      const q = query(
        collection(db, 'reports'),
        where('status', 'in', statusFilter),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Report, 'id'>) }));
      setReports(rows);

      // Bulk-fetch user chips for reporters + target owners (deduped).
      const uids = new Set<string>();
      rows.forEach((r) => {
        if (r.reporterId) uids.add(r.reporterId);
        if (r.targetOwnerId) uids.add(r.targetOwnerId);
      });
      const fresh: Record<string, UserChip> = {};
      await Promise.all(
        Array.from(uids).map(async (uid) => {
          if (users[uid]) {
            fresh[uid] = users[uid];
            return;
          }
          const d = await getDoc(doc(db, 'users', uid));
          if (d.exists()) {
            const data = d.data() as any;
            fresh[uid] = {
              uid,
              username: data.username,
              displayName: data.displayName ?? data.profile?.displayName,
              avatarUrl: data.avatarUrl ?? data.profile?.avatarUrl,
              moderationState: data.moderationState,
            };
          } else {
            fresh[uid] = { uid };
          }
        })
      );
      setUsers((prev) => ({ ...prev, ...fresh }));
    } catch (err) {
      console.error('loadReports failed', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, tab]);

  // Group pending reports by targetOwnerId so admins see all reports against
  // one user clustered — common case is a single user being reported multiple
  // times by different reporters in a short window.
  const grouped = useMemo(() => {
    if (tab !== 'pending') return null;
    const map = new Map<string, Report[]>();
    for (const r of reports) {
      const list = map.get(r.targetOwnerId) ?? [];
      list.push(r);
      map.set(r.targetOwnerId, list);
    }
    return Array.from(map.entries());
  }, [reports, tab]);

  // ---- Actions ----------------------------------------------------------

  const resolveReport = async (
    reportId: string,
    status: 'resolved' | 'dismissed',
    notes?: string
  ) => {
    if (!currentUser) return;
    setBusy(reportId);
    try {
      await updateDoc(doc(db, 'reports', reportId), {
        status,
        resolvedAt: serverTimestamp(),
        resolvedBy: currentUser,
        ...(notes ? { resolution: notes } : {}),
      });
      setReports((prev) => prev.filter((r) => r.id !== reportId));
    } catch (err) {
      console.error('resolveReport failed', err);
      alert('Could not update the report — check console for details.');
    } finally {
      setBusy(null);
    }
  };

  /**
   * Suspend or ban the target user by writing `moderationState` on their
   * user doc. Firestore rules read this to block writes across every
   * abuse-prone path (items, creations, messages, reports, confirms).
   * Also resolves the current report with a note so the queue clears.
   *
   * The onModerationStateChange Cloud Function fires on this write and
   * drops an in-app notification into the affected user's notifications,
   * so they learn from the app rather than being confused by silent failures.
   */
  const moderateUser = async (
    r: Report,
    state: 'suspended' | 'banned'
  ) => {
    if (!currentUser) return;
    const label = state === 'banned' ? 'BAN' : 'SUSPEND';
    const target = users[r.targetOwnerId];
    const nameForConfirm =
      target?.displayName ?? target?.username ?? r.targetOwnerId;
    if (
      !confirm(
        `${label} ${nameForConfirm}?\n\n` +
          (state === 'banned'
            ? 'The user will see a "banned" screen at signin and cannot post, message, or list items anywhere in the app.'
            : 'The user can still browse and read, but cannot post, message, or list items until you clear the state.')
      )
    ) {
      return;
    }
    setBusy(r.id);
    try {
      await updateDoc(doc(db, 'users', r.targetOwnerId), {
        moderationState: state,
        moderationStateSetAt: serverTimestamp(),
        moderationStateSetBy: currentUser,
      });
      // Reflect locally so the badge updates immediately without a
      // reload — the next tab switch/refetch will re-read from Firestore.
      setUsers((prev) => ({
        ...prev,
        [r.targetOwnerId]: {
          ...(prev[r.targetOwnerId] ?? { uid: r.targetOwnerId }),
          moderationState: state,
        },
      }));
      const noteFallback =
        state === 'banned'
          ? 'User banned by admin.'
          : 'User suspended by admin.';
      await resolveReport(r.id, 'resolved', notesDraft[r.id] || noteFallback);
    } catch (err) {
      console.error('moderateUser failed', err);
      alert(`Could not ${state} the user — check console for details.`);
      setBusy(null);
    }
  };

  /**
   * Hide the reported content by soft-deleting or flagging the target doc.
   *
   *   item     → users/{ownerId}/items/{targetId}.isDeleted = true
   *   creation → users/{ownerId}/creations/{targetId}.isDeleted = true
   *   message  → conversations/{conversationId}/messages/{targetId}.isHidden = true
   *              (conversationId lives in report.context.conversationId)
   *   photo    → find the photo across the owner's items by URL, flip its
   *              dmcaStatus to 'hidden' (existing schema; reads already
   *              filter hidden/removed/flagged photos out of community
   *              aggregations via DMCA_HIDDEN in firebase/db.ts)
   *   profile  → still requires out-of-band handling (bio doesn't have a
   *              soft-delete flag; use Suspend user for account-level action)
   */
  const hideContent = async (r: Report) => {
    if (!currentUser) return;
    if (!confirm(`Hide this ${TARGET_TYPE_META[r.targetType].label.toLowerCase()}? The owner can't undo this from the app.`)) return;
    setBusy(r.id);
    try {
      if (r.targetType === 'item') {
        await updateDoc(doc(db, 'users', r.targetOwnerId, 'items', r.targetId), {
          isDeleted: true,
          deletedAt: serverTimestamp(),
          deletedByAdmin: true,
        });
      } else if (r.targetType === 'creation') {
        await updateDoc(doc(db, 'users', r.targetOwnerId, 'creations', r.targetId), {
          isDeleted: true,
          deletedAt: serverTimestamp(),
          deletedByAdmin: true,
        });
      } else if (r.targetType === 'message') {
        // Chat messages live under messages/{conversationId}/entries/{id}.
        // The reporter attached conversationId in report.context so we can
        // reach the right doc without scanning every conversation.
        const conversationId = (r.context as { conversationId?: string } | undefined)?.conversationId;
        if (!conversationId) {
          alert(
            'This message report is missing conversationId in its context — the app version that generated it predates the context field. Handle out-of-band.'
          );
          setBusy(null);
          return;
        }
        await updateDoc(doc(db, 'conversations', conversationId, 'messages', r.targetId), {
          isHidden: true,
          hiddenAt: serverTimestamp(),
          hiddenByAdmin: true,
        });
      } else if (r.targetType === 'photo') {
        // targetId is the photo URL. Photos live embedded in the owner's
        // items or creations; we scan both to find which parent doc has
        // the matching photo, then update that photo entry's dmcaStatus.
        // Existing community aggregation reads filter hidden photos out.
        const hidden = await hidePhotoByUrl(r.targetOwnerId, r.targetId);
        if (!hidden) {
          alert(
            "Couldn't locate the reported photo in the owner's items or creations. It may have already been deleted."
          );
          setBusy(null);
          return;
        }
      } else {
        alert(
          `This target type (${r.targetType}) doesn't have a soft-delete flag yet.\n` +
            'Resolve with a note describing the action you took out-of-band (e.g. emailed the user), or use Suspend/Ban.'
        );
        setBusy(null);
        return;
      }
      await resolveReport(r.id, 'resolved', notesDraft[r.id] || 'Content hidden by admin.');
    } catch (err) {
      console.error('hideContent failed', err);
      alert('Could not hide the content — check console for details.');
      setBusy(null);
    }
  };

  /**
   * Locate a photo by URL across an owner's items + creations and flip
   * its dmcaStatus to 'hidden'. Returns true if a match was found and
   * updated. Iterates client-side because Firestore doesn't support
   * array-contains-object queries.
   */
  const hidePhotoByUrl = async (ownerId: string, photoUrl: string): Promise<boolean> => {
    const surfaces: Array<'items' | 'creations'> = ['items', 'creations'];
    for (const surface of surfaces) {
      const snap = await getDocs(collection(db, 'users', ownerId, surface));
      for (const parent of snap.docs) {
        const data = parent.data() as { photos?: Array<{ url?: string; dmcaStatus?: string }> };
        const photos = Array.isArray(data.photos) ? data.photos : [];
        const idx = photos.findIndex((p) => p?.url === photoUrl);
        if (idx === -1) continue;
        const updatedPhotos = photos.map((p, i) =>
          i === idx ? { ...p, dmcaStatus: 'hidden' } : p
        );
        await updateDoc(doc(db, 'users', ownerId, surface, parent.id), {
          photos: updatedPhotos,
        });
        return true;
      }
    }
    return false;
  };

  // ---- Render -----------------------------------------------------------

  if (checkingAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="glass-card p-6 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-purple-400" />
          <p className="text-glass-secondary">Checking admin status...</p>
        </div>
      </div>
    );
  }

  if (!currentUser || !isAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="glass-card p-6 text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-red-400" />
          <h1 className="text-2xl font-bold text-glass mb-2 font-heading">
            Admin Access Required
          </h1>
          <p className="text-glass-secondary font-body">
            You must be logged in as an admin to view this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8">
      <Link
        to="/admin"
        className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 mb-4 font-body"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Admin Dashboard
      </Link>

      <div className="bg-gradient-to-r from-red-600 to-purple-600 rounded-xl shadow-lg p-6 mb-6 text-white">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-10 h-10" />
          <div>
            <h1 className="text-2xl font-bold font-heading">Moderation Queue</h1>
            <p className="opacity-90 font-body">
              User-submitted reports across the app — triage within 24 hours.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {(['pending', 'resolved'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-full font-body-bold text-sm capitalize ${
              tab === t
                ? 'bg-purple-600 text-white'
                : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700/60'
            }`}
          >
            {t}
            {tab === t && reports.length > 0 ? ` (${reports.length})` : ''}
          </button>
        ))}
        <button
          onClick={loadReports}
          className="ml-auto px-4 py-2 rounded-full font-body-bold text-sm bg-slate-800/60 text-glass-secondary hover:bg-slate-700/60 flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="glass-card p-10 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-purple-400" />
        </div>
      ) : reports.length === 0 ? (
        <div className="glass-card p-10 text-center">
          <Check className="w-12 h-12 mx-auto mb-3 text-emerald-300" />
          <h2 className="text-xl font-heading text-glass mb-1">All clear</h2>
          <p className="text-glass-secondary font-body">
            {tab === 'pending'
              ? 'No pending reports right now.'
              : 'No resolved reports yet.'}
          </p>
        </div>
      ) : tab === 'pending' && grouped ? (
        <div className="space-y-4">
          {grouped.map(([ownerId, ownerReports]) => {
            const owner = users[ownerId];
            return (
              <div key={ownerId} className="glass-card p-4">
                <div className="flex items-center gap-3 mb-3 pb-3 border-b border-slate-700/50">
                  <UserAvatar user={owner} />
                  <div className="flex-1">
                    <div className="font-body-bold text-glass flex items-center flex-wrap gap-2">
                      <span>
                        {owner?.displayName ?? owner?.username ?? 'Unknown user'}
                      </span>
                      {owner?.username ? (
                        <span className="font-body text-glass-secondary text-sm">
                          @{owner.username}
                        </span>
                      ) : null}
                      <ModerationBadge state={owner?.moderationState} />
                    </div>
                    <div className="font-body text-glass-secondary text-xs">
                      {ownerReports.length} report{ownerReports.length === 1 ? '' : 's'}
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  {ownerReports.map((r) => (
                    <ReportRow
                      key={r.id}
                      report={r}
                      reporter={users[r.reporterId]}
                      busy={busy === r.id}
                      notes={notesDraft[r.id] ?? ''}
                      onNotesChange={(v) =>
                        setNotesDraft((prev) => ({ ...prev, [r.id]: v }))
                      }
                      onDismiss={() => resolveReport(r.id, 'dismissed', notesDraft[r.id])}
                      onResolve={() => resolveReport(r.id, 'resolved', notesDraft[r.id])}
                      onHide={() => hideContent(r)}
                      onSuspend={() => moderateUser(r, 'suspended')}
                      onBan={() => moderateUser(r, 'banned')}
                      ownerModerationState={owner?.moderationState}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <div key={r.id} className="glass-card p-4">
              <ReportRow
                report={r}
                reporter={users[r.reporterId]}
                resolved
              />
              {r.resolution ? (
                <div className="mt-3 pt-3 border-t border-slate-700/50 font-body text-sm text-glass-secondary">
                  <span className="font-body-bold text-glass">Resolution notes:</span> {r.resolution}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Subcomponents --------------------------------------------------------

function UserAvatar({ user }: { user?: UserChip }) {
  if (!user) return <div className="w-10 h-10 rounded-full bg-slate-700" />;
  if (user.avatarUrl) {
    return (
      <img
        src={user.avatarUrl}
        alt=""
        className="w-10 h-10 rounded-full object-cover"
      />
    );
  }
  return (
    <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-body-bold">
      {(user.displayName ?? user.username ?? '?').charAt(0).toUpperCase()}
    </div>
  );
}

interface ReportRowProps {
  report: Report;
  reporter?: UserChip;
  resolved?: boolean;
  busy?: boolean;
  notes?: string;
  onNotesChange?: (v: string) => void;
  onDismiss?: () => void;
  onResolve?: () => void;
  onHide?: () => void;
  onSuspend?: () => void;
  onBan?: () => void;
  /** Current moderationState of the target owner. Used to disable the
   *  matching button (no point suspending an already-suspended user)
   *  and to show a status badge inline with the actions. */
  ownerModerationState?: 'active' | 'suspended' | 'banned';
}

function ReportRow({
  report,
  reporter,
  resolved,
  busy,
  notes,
  onNotesChange,
  onDismiss,
  onResolve,
  onHide,
  onSuspend,
  onBan,
  ownerModerationState,
}: ReportRowProps) {
  const meta = TARGET_TYPE_META[report.targetType];
  const Icon = meta.Icon;

  return (
    <div className="rounded-xl bg-slate-900/40 border border-slate-700/40 p-3">
      <div className="flex items-start gap-3 mb-2">
        <Icon className={`w-5 h-5 mt-0.5 ${meta.color}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-body-bold text-glass text-sm">
              {REASON_LABEL[report.reason]}
            </span>
            <span className="font-body text-glass-secondary text-xs">
              · {meta.label}
            </span>
            <span className="font-body text-glass-secondary text-xs ml-auto">
              {formatDate(report.createdAt)}
            </span>
          </div>
          <div className="font-body text-glass-secondary text-xs mt-0.5">
            Reported by{' '}
            <span className="font-body-bold text-glass">
              {reporter?.displayName ?? reporter?.username ?? 'Unknown'}
            </span>
            {' · target id '}
            <code className="text-xs">{report.targetId}</code>
          </div>
        </div>
      </div>

      {report.detail ? (
        <div className="font-body text-glass text-sm mt-2 p-2 rounded bg-slate-900/60">
          "{report.detail}"
        </div>
      ) : null}

      {report.context?.conversationId ? (
        <div className="mt-2 font-body text-xs text-glass-secondary">
          Context: conversation <code>{String(report.context.conversationId)}</code>
        </div>
      ) : null}

      {!resolved ? (
        <div className="mt-3 space-y-2">
          {onNotesChange ? (
            <textarea
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              placeholder="Resolution notes (optional) — what action was taken"
              rows={2}
              className="w-full px-3 py-2 rounded-lg bg-slate-950/40 border border-slate-700/50 text-glass font-body text-sm"
            />
          ) : null}
          <div className="flex flex-wrap gap-2">
            <button
              disabled={busy}
              onClick={onDismiss}
              className="px-3 py-1.5 rounded-full bg-slate-700 hover:bg-slate-600 text-white font-body-bold text-sm flex items-center gap-1.5 disabled:opacity-50"
            >
              <X className="w-4 h-4" />
              Dismiss
            </button>
            {(report.targetType === 'item' || report.targetType === 'creation') ? (
              <button
                disabled={busy}
                onClick={onHide}
                className="px-3 py-1.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white font-body-bold text-sm flex items-center gap-1.5 disabled:opacity-50"
              >
                <EyeOff className="w-4 h-4" />
                Hide content + resolve
              </button>
            ) : null}
            <button
              disabled={busy}
              onClick={onResolve}
              className="px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-body-bold text-sm flex items-center gap-1.5 disabled:opacity-50"
            >
              <Check className="w-4 h-4" />
              Resolve
            </button>
            {/* User-level moderation actions. These write moderationState
                on the reported user's doc. Firestore rules read that field
                to block the user from creating items/messages/reports/etc.
                Suspend = amber (recoverable timeout), Ban = red (terminal). */}
            {onSuspend ? (
              <button
                disabled={busy || ownerModerationState === 'suspended' || ownerModerationState === 'banned'}
                onClick={onSuspend}
                title={
                  ownerModerationState === 'suspended'
                    ? 'User is already suspended'
                    : ownerModerationState === 'banned'
                      ? 'User is already banned'
                      : 'Suspend this user — they cannot post, message, or list items'
                }
                className="px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-body-bold text-sm flex items-center gap-1.5 disabled:opacity-50"
              >
                <Clock className="w-4 h-4" />
                Suspend user
              </button>
            ) : null}
            {onBan ? (
              <button
                disabled={busy || ownerModerationState === 'banned'}
                onClick={onBan}
                title={
                  ownerModerationState === 'banned'
                    ? 'User is already banned'
                    : 'Ban this user — they will see a "banned" screen at signin'
                }
                className="px-3 py-1.5 rounded-full bg-red-600 hover:bg-red-700 text-white font-body-bold text-sm flex items-center gap-1.5 disabled:opacity-50"
              >
                <Ban className="w-4 h-4" />
                Ban user
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Small pill next to a user's name showing their current moderation state.
 * Absent/`active` renders nothing so normal users don't get a "clean" badge
 * that would visually equate them with the moderated ones.
 */
function ModerationBadge({
  state,
}: {
  state?: 'active' | 'suspended' | 'banned';
}) {
  if (!state || state === 'active') return null;
  const isBanned = state === 'banned';
  const classes = isBanned
    ? 'bg-red-500/25 text-red-200 border-red-500/40'
    : 'bg-amber-500/25 text-amber-200 border-amber-500/40';
  const Icon = isBanned ? Ban : Clock;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${classes} font-body-bold text-[11px] uppercase tracking-wide`}
    >
      <Icon className="w-3 h-3" />
      {state}
    </span>
  );
}
